import React, { useState, useEffect } from 'react';

/**
 * Motivational Progress Component
 * Shows inspiring quotes and progress messages during long operations
 * Makes waiting time enjoyable and engaging for customers
 */
const MotivationalProgress = ({ 
  isVisible = false, 
  stage = 'processing', 
  progress = 0, 
  customMessage = null,
  onClose = null,
  showFunFacts = true,
  duration = null // estimated duration in seconds
}) => {
  const [currentQuote, setCurrentQuote] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentEmoji, setCurrentEmoji] = useState('💫');
  const [funFact, setFunFact] = useState('');
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [showExtraEncouragement, setShowExtraEncouragement] = useState(false);

  // Motivational quotes organized by processing stage
  const quotes = {
    starting: [
      "Take a deep breath... every journey begins with a single step! 🌟",
      "Great things never come from comfort zones. Let's begin! 💪",
      "The best time to plant a tree was 20 years ago. The second best time is now! 🌱",
      "Ready, set, grow! Let's turn knowledge into wisdom! 📚"
    ],
    scraping: [
      "When life gives you websites, we make knowledge! 🍋➡️🧠",
      "Like a spider weaving its web, we're gathering your content carefully! 🕷️",
      "Rome wasn't built in a day, and great content takes time to collect! 🏛️",
      "We're mining digital gold from your website! ⛏️✨",
      "Good things come to those who wait... and we're making it worth the wait! 🎁"
    ],
    analyzing: [
      "Life is 10% what happens to you and 90% how you react to it. We're reacting with intelligence! 🧠",
      "Like a detective solving a mystery, we're uncovering the story in your content! 🔍",
      "Turning information into insight, one byte at a time! 💡",
      "Breaking down complexity into simple, beautiful understanding! 🧩"
    ],
    embedding: [
      "Transforming words into mathematical poetry! 🎭➡️🔢",
      "We're teaching AI to understand your content like a human would! 🤖❤️",
      "Converting thoughts into vectors... it's like digital telepathy! 🧠📡",
      "Like translating languages, we're translating content into AI understanding! 🗣️➡️🤖"
    ],
    indexing: [
      "Organizing knowledge is like arranging stars in the sky! ⭐",
      "Creating your personal library of instant answers! 📚",
      "Like a librarian organizing books, we're organizing your wisdom! 📖",
      "Your content is getting VIP treatment in our knowledge database! 👑"
    ],
    searching: [
      "Seeking and you shall find! We're on the hunt for perfect answers! 🎯",
      "Like finding a needle in a haystack, but we have the world's best magnet! 🧲",
      "Swimming through oceans of data to find your pearl of wisdom! 🏊‍♀️💎",
      "Your question is our mission, and failure is not an option! 🚀"
    ],
    generating: [
      "Crafting your answer like a master chef preparing a perfect dish! 👨‍🍳",
      "Like a composer creating a symphony, we're composing your answer! 🎵",
      "Turning data into wisdom, information into insight! ✨",
      "Mixing intelligence with understanding to create the perfect response! 🧪"
    ],
    completing: [
      "Success is not final, failure is not fatal, but your answer is ready! 🎉",
      "Like watching a sunrise, your answer is about to brighten your day! 🌅",
      "The cherry on top is ready! Your complete answer awaits! 🍒",
      "Mission accomplished! Your question has met its perfect match! ✅"
    ],
    processing: [
      "Patience is not the ability to wait, but the ability to keep a good attitude while waiting! ⏳",
      "Great minds think alike, and we're thinking about your question! 🤔",
      "Every second of waiting is an investment in a perfect answer! 💰",
      "Like a caterpillar becoming a butterfly, transformation takes time! 🦋"
    ],
    error: [
      "When life gives you errors, we debug them into success! 🐛➡️✨",
      "Every problem is an opportunity in disguise! 🎭",
      "Obstacles are stepping stones to success! 🪨➡️🏆",
      "This is just a plot twist in your success story! 📖"
    ]
  };

  // Status messages for each stage
  const statusMessages = {
    starting: "Getting ready to process your request...",
    scraping: "Extracting content from your website...",
    analyzing: "Analyzing and cleaning the content...",
    embedding: "Converting content to AI-readable format...",
    indexing: "Organizing content in knowledge database...",
    searching: "Finding the most relevant information...",
    generating: "Crafting your personalized answer...",
    completing: "Finalizing your response...",
    processing: "Processing in progress...",
    error: "Encountering a small hiccup, but we'll handle it!"
  };

  // Fun facts to show during longer waits
  const funFacts = [
    "Did you know? The first computer bug was an actual bug found in 1947! 🐛",
    "Fun fact: AI can now write poetry, but it still can't fold fitted sheets! 📝",
    "Amazing: Your brain processes information at about 120 meters per second! 🧠",
    "Cool fact: There are more possible chess games than atoms in the universe! ♟️",
    "Incredible: Octopuses have three hearts and blue blood! 🐙",
    "Amazing: Your smartphone has more computing power than NASA's 1969 moon mission! 📱"
  ];

  // Long wait encouragements
  const longWaitEncouragements = [
    "Great things take time! We're crafting something amazing for you! ⏰✨",
    "Like a fine wine, your answer is getting better with every passing moment! 🍷",
    "Think of this as meditation time. Deep breaths... your answer is almost ready! 🧘‍♀️",
    "Good things come to those who wait, but better things come to those who are patient! 🎁"
  ];

  // Get emoji for stage
  const getStageEmoji = (stage) => {
    const emojis = {
      starting: "🚀", scraping: "🕷️", analyzing: "🔍", embedding: "🧠",
      indexing: "📚", searching: "🎯", generating: "✨", completing: "🎉",
      processing: "⏳", error: "🔧"
    };
    return emojis[stage] || "💫";
  };

  // Update quote and status when stage changes
  useEffect(() => {
    const stageQuotes = quotes[stage] || quotes.processing;
    const randomQuote = stageQuotes[Math.floor(Math.random() * stageQuotes.length)];
    setCurrentQuote(randomQuote);
    setCurrentStatus(customMessage || statusMessages[stage] || "Processing...");
    setCurrentEmoji(getStageEmoji(stage));
  }, [stage, customMessage]);

  // Timer for elapsed time and encouragement
  useEffect(() => {
    if (!isVisible) {
      setTimeElapsed(0);
      setShowExtraEncouragement(false);
      return;
    }

    const timer = setInterval(() => {
      setTimeElapsed(prev => {
        const newTime = prev + 1;
        
        // Show extra encouragement after 10 seconds
        if (newTime === 10) {
          setShowExtraEncouragement(true);
        }
        
        // Change quote every 8 seconds for variety
        if (newTime % 8 === 0) {
          const stageQuotes = quotes[stage] || quotes.processing;
          const randomQuote = stageQuotes[Math.floor(Math.random() * stageQuotes.length)];
          setCurrentQuote(randomQuote);
        }
        
        // Show fun facts every 15 seconds
        if (newTime % 15 === 0 && showFunFacts) {
          const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
          setFunFact(randomFact);
        }
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible, stage, showFunFacts]);

  if (!isVisible) return null;

  const progressBarWidth = Math.min(progress, 100);
  const estimatedTimeLeft = duration ? Math.max(duration - timeElapsed, 0) : null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 relative">
        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
            aria-label="Close"
          >
            ×
          </button>
        )}

        {/* Main emoji and status */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-3 animate-bounce">
            {currentEmoji}
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {currentStatus}
            {progress > 0 && ` (${Math.round(progress)}%)`}
          </h3>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressBarWidth}%` }}
            ></div>
          </div>
          {estimatedTimeLeft !== null && (
            <div className="text-center text-sm text-gray-600">
              Estimated time remaining: {estimatedTimeLeft}s
            </div>
          )}
        </div>

        {/* Motivational quote */}
        <div className="text-center mb-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border-l-4 border-purple-500">
            <p className="text-gray-700 italic text-sm leading-relaxed">
              {currentQuote}
            </p>
          </div>
        </div>

        {/* Fun fact */}
        {funFact && (
          <div className="text-center mb-4">
            <div className="bg-yellow-50 rounded-lg p-3 border-l-4 border-yellow-400">
              <p className="text-gray-600 text-xs">
                {funFact}
              </p>
            </div>
          </div>
        )}

        {/* Extra encouragement for long waits */}
        {showExtraEncouragement && (
          <div className="text-center mb-4">
            <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-400">
              <p className="text-green-700 text-sm font-medium">
                {longWaitEncouragements[Math.floor(Math.random() * longWaitEncouragements.length)]}
              </p>
            </div>
          </div>
        )}

        {/* Elapsed time */}
        <div className="text-center text-xs text-gray-500">
          Processing time: {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
        </div>

        {/* Animated dots */}
        <div className="flex justify-center space-x-1 mt-4">
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div>
    </div>
  );
};

export default MotivationalProgress;